import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, ChevronRight } from '../../icons';
import {
  fetchCompanyCodeSets, saveCompanyCodes,
  type CodeDomain, type CompanyCodeDto, type CompanyCodeSetDto,
} from '../../api/company-codes';
import { resetCompanyCodes } from '../../lib/company-codes';
// 레일 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7b — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './CodesPage.css';

/**
 * 기준 설정 — **회사가 정하는 값들** (2026-08-28 · 자리 재정의 판 7).
 *
 * ── 무엇을 고치는 화면인가 ───────────────────────────────────────────────
 * 휴가 종류 · 출장 종류 · 고용형태 · 퇴사 사유 · 증명서 종류 · 포상/징계 종류 · 자격 종류.
 * 지금까지 이 값들은 **코드에 박혀 있어** 회사가 「경조」를 「경조휴가」로 부르고 싶어도
 * 고칠 데가 없었다. 여기가 그 자리다.
 *
 * ── 세 가지 안전선 ───────────────────────────────────────────────────────
 * ★★ **표가 비면 지금과 똑같다.** 바닥은 서버 코드가 가진 기본 묶음이고, 표는 그 위에 얹는다
 *    (m139 「회사가 템플릿에 보탠 항목」·메뉴 권한 「규칙 없으면 지금대로」와 같은 규칙).
 * ★★ **기본 값은 지울 수 없고 «안 씀» 으로만 둔다.** 지난 기록이 그 값을 가리키고 있어서,
 *    빼 버리면 지난 휴가가 「무엇이었는지」 를 읽을 수 없게 된다(직위 마스터의 «접기» 와 같다).
 * ★★ **여는 것은 «이름» 과 «씀/안 씀» 이다 — 값을 보태는 것은 아직 아니다.** 일곱 다 서버
 *    스키마의 닫힌 enum 이라, 보탠 값은 고를 수는 있어도 신청이 400 이 된다. 「고를 수 있는데
 *    안 되는 것」이 제일 나쁘다(반반차를 넣을 때 이미 겪었다). 서버 쪽이 열리면 그 목록에만
 *    `addable` 이 켜지고, 이 화면은 그때부터 「값 추가」를 그린다.
 * ★★ **목록 자체는 회사가 못 만든다.** 새 목록은 그 값을 읽어 무언가를 하는 코드가 함께
 *    있어야 뜻이 생긴다 — 값만 늘고 아무 일도 안 일어나면 그것이 더 나쁜 거짓말이다.
 *
 * ── 누가 고치나 ─────────────────────────────────────────────────────────
 * **읽기는 전 직원**(신청 화면의 선택지라 못 읽으면 휴가를 못 고른다), **쓰기는 담당자**.
 * 판정은 서버가 한다 — 근태 목록은 `attendance`, 인사 목록은 `hr` 메뉴를 받은 사람.
 * 그래서 이 화면에는 역할 가드가 없다: 못 고치는 사람에게는 **읽기 전용**으로 보인다.
 */

interface Props {
  domain: CodeDomain;
  /**
   * 왼쪽 레일의 제목 — **메뉴 이름과 같아야 한다**(트리에 적힌 이름을 보고 누르기 때문이다).
   * ★ 안 주면 종전 그대로 「기준 설정」이다 — 근태·인사 트리는 그 이름으로 서 있다.
   * ★ 결재는 목록이 **계정과목 하나뿐**이라 트리에도 그 이름으로 선다(시안 F2) — 그래서
   *   여기서도 그렇게 적는다. 목록이 늘면 이름을 「기준 설정」으로 되돌린다.
   */
  title?: string;
  /**
   * 「여기서 고치지 않는 것」 — 제 화면이 따로 있는 목록. 인사 쪽 직위·직급이 그렇다
   * (m138·m139 의 화면이 이미 있고, 이리로 옮기면 한 값을 두 자리에서 고치게 된다).
   */
  away?: ReadonlyArray<{ to: string; label: string; hint: string }>;
}

/** 편집 중인 한 줄 — 서버가 준 것에 «새로 보탠 것» 을 섞는다. */
interface Row extends CompanyCodeDto { fresh?: boolean }

const CODE_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/i;

/** 기준 화면의 주소 — 경로 · 범위 칩을 메뉴 배치에서 찾는 열쇠(web 레인 W7b). */
const CODES_PATH: Record<CodeDomain, string> = {
  approval: '/approval/admin/codes',
  attendance: '/erp/attendance/codes',
  hr: '/erp/hr/codes',
};

export function CodesPage({ domain, away, title = '기준 설정' }: Props): JSX.Element {
  const [sets, setSets] = useState<CompanyCodeSetDto[] | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * 목록 고르기 — 편집 중이던 줄을 버리고 서버가 준 값으로 되돌린다.
   *
   * ★ 이것을 «active 가 바뀌면» 도는 effect 로 두면 **저장 직후 알림이 사라진다** —
   *   저장이 `sets` 를 갈아 끼우면서 `active` 의 정체가 바뀌어 effect 가 한 번 더 돌기 때문이다.
   *   목록을 바꾸는 자리는 둘뿐이라(첫 그리기 · 왼쪽 단추) 그 둘에서 직접 부른다.
   */
  const pick = useCallback((s: CompanyCodeSetDto) => {
    setActiveKey(s.key);
    setRows(s.codes.map((c) => ({ ...c })));
    setDirty(false);
    setError(null);
    setNotice(null);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await fetchCompanyCodeSets(domain);
        if (!alive) return;
        setSets(list);
        if (list[0]) pick(list[0]);
        setState('ok');
      } catch {
        // 옛 서버는 이 라우트를 모른다(404) — 화면이 그 사실을 말하고 접힌다.
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [domain]);

  const active = useMemo(
    () => sets?.find((s) => s.key === activeKey) ?? null,
    [sets, activeKey],
  );


  /** 한 줄 고치기 — **자리(index)로** 찾는다. 새 줄은 값이 아직 비어 있어 code 로는 못 찾는다. */
  const patch = useCallback((index: number, next: Partial<Row>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...next } : r)));
    setDirty(true);
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { code: '', label: '', active: true, builtin: false, fresh: true }]);
    setDirty(true);
  }, []);

  const removeRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!active) return;
    const trimmed = rows.map((r) => ({ ...r, code: r.code.trim(), label: r.label.trim() }));

    const empty = trimmed.find((r) => !r.code);
    if (empty) { setError('값을 비워 둘 수 없습니다.'); return; }
    const bad = trimmed.find((r) => !CODE_RE.test(r.code));
    if (bad) { setError(`값은 영문·숫자로 씁니다 — 「${bad.code}」`); return; }
    const seen = new Set<string>();
    const dup = trimmed.find((r) => (seen.has(r.code) ? true : (seen.add(r.code), false)));
    if (dup) { setError(`같은 값이 둘입니다 — 「${dup.code}」`); return; }

    setBusy(true);
    setError(null);
    try {
      const codes = await saveCompanyCodes(
        active.key,
        trimmed.map((r) => ({ code: r.code, label: r.label || null, active: r.active })),
      );
      setSets((prev) => prev?.map((s) => (s.key === active.key ? { ...s, codes } : s)) ?? prev);
      setRows(codes.map((c) => ({ ...c })));
      // ★ 쓰는 쪽(신청 화면)이 이고 있던 옛 이름을 버린다 — 안 버리면 이 탭에서만 안 바뀐다.
      resetCompanyCodes();
      setDirty(false);
      setNotice('저장했습니다.');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? '이 목록을 고칠 권한이 없습니다.' : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [active, rows]);

  if (state === 'loading') return <div className="codes__fold">불러오는 중…</div>;
  if (state === 'folded' || !sets) {
    return <div className="codes__fold">{title} — 아직 쓸 수 없습니다.</div>;
  }

  return (
    <div className="codes-page">
      {/* 화면 제목은 정본 머리가 진다(2026-10-17 · web 레인 W7b) — 종전에는 왼쪽 목록 맨 위 <h1> 이었다.
          ★ 제목은 메뉴 이름과 같아야 한다(`lib/hr-menu-titles.test.ts` 가 title 기본값을 읽는다). */}
      <PageHeader
        breadcrumb={crumbsFor(CODES_PATH[domain])}
        scope={scopeFor(CODES_PATH[domain])}
        title={title}
      />
    <div className="codes">
      <nav className="codes__rail" aria-label={`${title} 목록`}>
        {sets.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`codes__pick${s.key === activeKey ? ' is-on' : ''}`}
            aria-current={s.key === activeKey ? 'true' : undefined}
            onClick={() => pick(s)}
          >
            <span className="codes__picknm">{s.label}</span>
            {/* ★ 물러난 값은 고르는 목록에 안 나오므로 「몇 개 씀」에서도 뺀다(S6 · 2026-09-16). */}
            <span className="codes__pickct">{s.codes.filter((c) => c.active && !c.retired).length}개 씀</span>
          </button>
        ))}

        {away?.map((a) => (
          <Link key={a.to} className="codes__away" to={a.to}>
            <span>{a.label}</span>
            <ChevronRight size={15} aria-hidden />
          </Link>
        ))}
      </nav>

      {active && (
        <section className="codes__panel" aria-label={active.label}>
          <div className="codes__head">
            {/*
              ★ **왼쪽 머리와 같은 낱말이면 여기서 또 적지 않는다** (2026-08-31 · 결재 계정과목).
                결재는 목록이 하나뿐이라 화면 제목이 곧 그 목록 이름이다 — 그대로 두면
                같은 낱말이 한 화면에 셋이 된다. 근태·인사는 제목이 「기준 설정」이라 안 겹친다.
            */}
            {active.label !== title && <h2 className="codes__title">{active.label}</h2>}
            <span className="codes__hint">{active.hint}</span>
          </div>

          {error && <p className="codes__err" role="alert">{error}</p>}
          {notice && !error && <p className="codes__ok">{notice}</p>}

          <table className="codes__table">
            <thead>
              <tr>
                <th scope="col">값</th>
                <th scope="col">이름</th>
                <th scope="col">씀</th>
                <th scope="col"><span className="codes__sr">지우기</span></th>
              </tr>
            </thead>
            <tbody>
              {/*
                ★★ **바닥이 빈 목록이 생겼다** (2026-08-29 · 계정과목). 앞의 일곱은 코드가 가진
                   기본 묶음이 있어 표가 비는 일이 없었지만, 회사가 통째로 정하는 목록은
                   **처음에 한 줄도 없다** — 그때 머리글만 남으면 「고장인가」로 읽힌다.
              */}
              {rows.length === 0 && (
                <tr className="codes__row codes__row--none">
                  <td colSpan={4}>
                    {active.addable
                      ? '아직 정한 값이 없습니다 — 아래 「값 추가」로 만듭니다.'
                      : '이 목록에는 값이 없습니다.'}
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr
                  key={r.fresh ? `new-${i}` : r.code}
                  className={`codes__row${r.active ? '' : ' is-off'}${r.retired ? ' is-retired' : ''}`}
                >
                  <td>
                    {r.fresh ? (
                      <input
                        className="codes__lbl"
                        value={r.code}
                        placeholder="영문·숫자"
                        aria-label={`새 값 ${i + 1}`}
                        onChange={(e) => patch(i, { code: e.target.value })}
                      />
                    ) : (
                      <>
                        <code className="codes__code">{r.code}</code>
                        {/* 물러난 값 — 지난 기록을 풀어 주려고 남아 있는 이름이다. */}
                        {r.retired && <span className="codes__tag codes__tag--retired">물러난 값</span>}
                      </>
                    )}
                  </td>
                  <td>
                    <input
                      className="codes__lbl"
                      value={r.label}
                      aria-label={`${r.fresh ? `새 값 ${i + 1}` : r.code} 이름`}
                      onChange={(e) => patch(i, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    {/*
                      ★★ **물러난 값은 켜지지 않는다** (S6 · 2026-09-16). 서버가 고르는 목록에서 이미 걸러 내므로
                         여기서 켤 수 있게 두면 「켰는데 선택지에 안 나온다」가 된다. 까닭을 그 자리에 적는다.
                    */}
                    <label className="codes__sw">
                      <input
                        type="checkbox"
                        checked={r.active}
                        disabled={r.retired}
                        aria-label={`${r.label || r.code} 씀`}
                        aria-describedby={r.retired ? `codes-why-${r.code}` : undefined}
                        onChange={(e) => patch(i, { active: e.target.checked })}
                      />
                      {r.active ? '씀' : '안 씀'}
                    </label>
                    {r.retired && (
                      <span className="codes__why" id={`codes-why-${r.code}`}>
                        지난 기록을 풀어 주는 이름입니다 — 새로 고르는 목록에는 안 나옵니다.
                      </span>
                    )}
                  </td>
                  <td>
                    {/* ★ 기본 값에는 지우기가 없다 — 끄기만 된다(지난 기록이 그 값을 가리킨다). */}
                    {r.builtin ? (
                      <span className="codes__tag">기본</span>
                    ) : (
                      <button
                        type="button"
                        className="codes__del"
                        aria-label={`${r.label || r.code} 지우기`}
                        onClick={() => removeRow(i)}
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="codes__foot">
            {/* ★ 보탤 수 없는 목록에는 「값 추가」를 그리지 않는다 — 저장이 400 이 된다. */}
            {active.addable && (
              <button type="button" className="codes__add" onClick={addRow}>
                <Plus size={15} aria-hidden />
                값 추가
              </button>
            )}
            <button
              type="button"
              className="codes__save"
              disabled={!dirty || busy}
              onClick={() => { void save(); }}
            >
              저장
            </button>
          </div>

          <p className="codes__note">
            여기서 고치는 것은 <strong>이름</strong>과 <strong>씀 · 안 씀</strong> 둘입니다.
            이름을 바꾸면 신청 화면의 선택지가 함께 바뀌고, 「안 씀」 으로 두면 그 값이 선택지에서
            빠집니다 — 다만 <strong>지난 기록에는 그대로 남습니다</strong>(빼 버리면 지난 신청이
            무엇이었는지 읽을 수 없게 됩니다).
            {!active.addable && ' 값을 새로 보태는 것은 아직 안 됩니다 — 그 값으로 무엇을 하는지가 함께 정해져야 합니다.'}
          </p>
        </section>
      )}
    </div>
    </div>
  );
}

/**
 * 결재 › 기준 설정 — 계정과목 (2026-08-29 · 판 10).
 *
 * ★ 값을 **회사가 통째로 정하는** 첫 목록이다(바닥이 비어 있다). 그 값을 읽는 자리는
 *   판 9 서식의 「고르기」 칸 — 지출결의서 종류를 만들어 «계정과목 + 금액» 두 칸을 두면 돈다.
 * ★ 가드는 라우터가 아니라 **서버**다 — 회사 관리자가 아니면 이 화면은 읽기 전용으로 보인다.
 */
export function ApprovalCodesPage(): JSX.Element {
  /* ★ 제목이 「계정과목」인 것은 결재 트리에 그 이름으로 서기 때문이다(시안 F2) —
       메뉴 이름과 화면 제목이 갈리면 누른 사람이 다른 데 온 줄 안다. */
  return <CodesPage domain="approval" title="계정과목" />;
}

/** 근태 › 기준 설정 — 휴가 종류 · 출장 종류. */
export function AttendanceCodesPage(): JSX.Element {
  return <CodesPage domain="attendance" />;
}

/**
 * 인사 › 기준 설정 — 고용형태 · 퇴사 사유 · 증명서 · 포상/징계 · 자격.
 *
 * ★ 직위·직급은 **여기서 고치지 않는다** — m138·m139 의 제 화면이 이미 있다.
 *   이리로 옮기면 한 값을 두 자리에서 고치게 되므로, 주소로만 걸어 둔다.
 */
export function HrCodesPage(): JSX.Element {
  return (
    <CodesPage
      domain="hr"
      away={[{ to: '/erp/hr/job-grades', label: '직위 · 직급', hint: '따로 있는 화면' }]}
    />
  );
}
